/**
 * PaywallModal — WL-F1 Premium-Trigger 4 "Kanzlei-Übergabe" (modules-loop-b.md
 * WL-F1, build-plan-loop-b.md §6). Fires right after a mandant completes a
 * self-service DATEV export (`DatevExportPage`/`DatevExportDialog`, the
 * "Export-Moment"): the invoices are now DATEV-ready to hand to the tax
 * advisor — a high-intent conversion moment for Loop-B Kanzlei-verbundene
 * Mandanten specifically.
 *
 * IST-note (coder, changelog-loop-b-paywall-trigger.md): this is the FIRST
 * discrete `PaywallModal` component in the codebase. The pre-existing generic
 * invoice-count paywall ("Trigger 1-3" in modules-loop-b.md prose) is an inline
 * banner (`usageLimitPromo`, InvoiceDashboard.tsx/XRechnungDashboard.tsx/
 * MobileInvoiceDashboard.tsx) with NO modal and NO growth-event attribution —
 * left untouched (out of scope for WL-F1). `PaywallTrigger` below is
 * deliberately a single-member union so a later module can extend the SAME
 * modal to Triggers 1-3 instead of building a parallel paywall system.
 *
 * No dark pattern (Reviewer-Gate, modules-loop-b.md WL-F1 "Gates"): the exact
 * price is NEVER hardcoded here (Stripe prices are fetched live in
 * `SubscriptionPage`) — the CTA navigates to `/dashboard/abonnement`, where the
 * real, current price is shown before any commitment. Three equally-weighted
 * dismiss paths (✕ button, backdrop click, "Später" button) besides the CTA;
 * no countdown, no fake scarcity, no forced interaction.
 * `paywallTrigger4Suppression` prevents a nag-loop by cooling the SAME variant
 * down for 7 days after being shown. This modal itself is ALWAYS user-action-
 * triggered — either right after a completed DATEV export (`variant='export_
 * moment'`) or by an explicit click on `KanzleiUebergabeMonthEndBanner`'s CTA
 * (`variant='month_end'`) — it never auto-opens on a passive page load
 * (Reviewer Finding 2, s. `KanzleiUebergabeMonthEndBanner` module doc).
 *
 * @module components/premium/PaywallModal
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconClose, IconInvoice, IconAdvisor } from '../../icons';

import { logGrowthEventBestEffort } from '../../api/growthEventsApi';
import { markKanzleiUebergabePaywallShown, PaywallTrigger4Variant } from '../../utils/paywallTrigger4Suppression';

/** The (eventually 4-member) set of premium triggers — see module doc for why
 *  only Trigger 4 exists as a discrete union member today. */
export type PaywallTrigger = 'kanzlei_uebergabe';

export interface PaywallModalProps {
  /** Reserved for future triggers — currently always `'kanzlei_uebergabe'`. */
  trigger: PaywallTrigger;
  /** Which surface is presenting Trigger 4 — drives copy + the analytics label. */
  variant: PaywallTrigger4Variant;
  /** Closes the modal. Called on ✕, backdrop click, and the secondary "später" action. */
  onClose: () => void;
}

/**
 * WL-F1 Trigger-4 modal. Fires a best-effort `paywall_shown` growth-event once
 * on mount (server resolves `advisorUid` attribution — see growthEventsApi.ts
 * doc); the caller is responsible for the beta/premium visibility gate (this
 * component itself does not re-check `isLoopBBeta`/`isPremium` — it renders
 * unconditionally once mounted, matching the outer/inner gate pattern used by
 * its callers, e.g. `DatevExportPage`/`KanzleiUebergabeMonthEndBanner`).
 */
const PaywallModal: React.FC<PaywallModalProps> = ({ variant, onClose }) => {
  const { t } = useTranslation(['subscription', 'common']);

  useEffect(() => {
    markKanzleiUebergabePaywallShown(variant);
    logGrowthEventBestEffort({
      type: 'paywall_shown',
      trigger: `kanzlei_uebergabe_${variant}`,
    }).catch(() => {
      // logGrowthEventBestEffort never rejects — defensive no-op only.
    });
    // Fire exactly once per mount — `variant` is stable for the modal's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const titleKey =
    variant === 'export_moment'
      ? 'paywallKanzlei.exportMoment.title'
      : 'paywallKanzlei.monthEnd.title';
  const messageKey =
    variant === 'export_moment'
      ? 'paywallKanzlei.exportMoment.message'
      : 'paywallKanzlei.monthEnd.message';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-kanzlei-uebergabe-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-hairline bg-surface-raised shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-hairline p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600/20 text-accent-ink">
              <IconAdvisor size={20} />
            </span>
            <h2 id="paywall-kanzlei-uebergabe-title" className="text-lg font-semibold text-primary">
              {t(titleKey, 'Alles DATEV-fertig an Ihre Kanzlei übergeben')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close', { ns: 'common' })}
            className="text-muted transition-colors hover:text-primary"
          >
            <IconClose size={18} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-secondary">
            {t(
              messageKey,
              'Mit Premium erhalten Sie unbegrenzte Rechnungen und weitere Funktionen rund um die Übergabe an Ihre Kanzlei.',
            )}
          </p>
          <p className="mt-3 text-xs text-faint">
            {t('paywallKanzlei.priceNote', 'Aktuelle Preise finden Sie transparent auf der nächsten Seite — keine automatische Buchung.')}
          </p>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-hairline p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-secondary transition-colors hover:bg-surface-inset"
          >
            {t('paywallKanzlei.later', 'Später')}
          </button>
          <Link
            to="/dashboard/abonnement"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
          >
            <IconInvoice size={14} />
            {t('paywallKanzlei.cta', 'Premium ansehen')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
